import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Settings, Edit2, Trash2, ChevronDown, ChevronRight, CheckSquare, X, Upload, Download, Layers } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProcessMasterPage() {
  const { user, getAuthHeader } = useAuth();
  const [processes, setProcesses] = useState([]);
  const [open, setOpen] = useState(false);
  const [editProcess, setEditProcess] = useState(null);
  const [expandedProcess, setExpandedProcess] = useState(null); // Single expanded process (accordion style)
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deleteProcess, setDeleteProcess] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    unit: 'units',
    checklist: [],
    benchmark_days: 0,
    target_time_hours: 0,
    sub_processes: []
  });

  const [newSubProcess, setNewSubProcess] = useState({ name: '', benchmark_qty_per_hour: '' });

  const fetchProcesses = async () => {
    try {
      const response = await axios.get(`${API_URL}/processes`, { headers: getAuthHeader() });
      setProcesses(response.data);
    } catch (error) {
      toast.error('Failed to fetch processes');
    }
  };

  useEffect(() => {
    fetchProcesses();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData
      };
      
      if (editProcess) {
        await axios.patch(`${API_URL}/processes/${editProcess.id}`, payload, { headers: getAuthHeader() });
        toast.success('Process updated successfully');
      } else {
        await axios.post(`${API_URL}/processes`, payload, { headers: getAuthHeader() });
        toast.success('Process created successfully');
      }
      setOpen(false);
      resetForm();
      fetchProcesses();
    } catch (error) {
      const errorDetail = error.response?.data?.detail; let errorMessage = 'Failed to save process'; if (typeof errorDetail === 'string') { errorMessage = errorDetail; } else if (Array.isArray(errorDetail)) { errorMessage = errorDetail.map(e => e.msg || JSON.stringify(e)).join(', '); } else if (errorDetail && typeof errorDetail === 'object') { errorMessage = errorDetail.msg || JSON.stringify(errorDetail); } toast.error(errorMessage);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', unit: 'units', checklist: [], benchmark_days: 0, target_time_hours: 0, sub_processes: [] });
    setEditProcess(null);
    setNewChecklistItem('');
    setNewSubProcess({ name: '', benchmark_qty_per_hour: '' });
  };

  const handleEdit = (process) => {
    setEditProcess(process);
    setFormData({
      name: process.name,
      description: process.description || '',
      unit: process.unit || 'units',
      checklist: process.checklist || [],
      benchmark_days: process.benchmark_days || 0,
      target_time_hours: process.target_time_hours || 0,
      sub_processes: process.sub_processes || []
    });
    setOpen(true);
  };

  const toggleExpand = (processId) => {
    setExpandedProcess(prev => prev === processId ? null : processId);
  };

  const addChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    setFormData({
      ...formData,
      checklist: [...formData.checklist, newChecklistItem.trim()]
    });
    setNewChecklistItem('');
  };

  const removeChecklistItem = (index) => {
    setFormData({
      ...formData,
      checklist: formData.checklist.filter((_, i) => i !== index)
    });
  };

  const addSubProcess = () => {
    if (!newSubProcess.name.trim()) return;
    setFormData({
      ...formData,
      sub_processes: [...formData.sub_processes, {
        name: newSubProcess.name.trim().toUpperCase(),
        benchmark_qty_per_hour: parseFloat(newSubProcess.benchmark_qty_per_hour) || 0
      }]
    });
    setNewSubProcess({ name: '', benchmark_qty_per_hour: '' });
  };

  const removeSubProcess = (index) => {
    setFormData({
      ...formData,
      sub_processes: formData.sub_processes.filter((_, i) => i !== index)
    });
  };

  const handleDelete = async () => {
    if (!deleteProcess) return;
    try {
      await axios.delete(`${API_URL}/processes/${deleteProcess.id}`, { headers: getAuthHeader() });
      toast.success(`Process '${deleteProcess.name}' deleted successfully`);
      setShowDeleteDialog(false);
      setDeleteProcess(null);
      fetchProcesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete process');
    }
  };

  const openDeleteDialog = (process) => {
    setDeleteProcess(process);
    setShowDeleteDialog(true);
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/processes/template/download`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'process_master_template.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/processes/bulk-upload`, formDataUpload, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      const { created, updated, errors, message } = response.data;
      toast.success(message || `Created ${created} processes, updated ${updated}`);
      if (errors?.length > 0) {
        toast.error(`${errors.length} errors occurred`);
        console.log('Upload errors:', errors);
      }
      fetchProcesses();
    } catch (error) {
      const errorDetail = error.response?.data?.detail; let errorMessage = 'Failed to upload file'; if (typeof errorDetail === 'string') { errorMessage = errorDetail; } else if (Array.isArray(errorDetail)) { errorMessage = errorDetail.map(e => e.msg || JSON.stringify(e)).join(', '); } else if (errorDetail && typeof errorDetail === 'object') { errorMessage = errorDetail.msg || JSON.stringify(errorDetail); } toast.error(errorMessage);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">You don&apos;t have permission to access this page</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-muted-foreground">Process Master Register - Define standard production processes with checklists</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadTemplate}>
              <Download className="w-4 h-4 mr-2" />
              Template
            </Button>
            <label>
              <Button variant="outline" disabled={uploading} asChild>
                <span>
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Bulk Upload'}
                </span>
              </Button>
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleBulkUpload} className="hidden" />
            </label>
            <Dialog open={open} onOpenChange={(val) => { setOpen(val); if (!val) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="create-process-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Process
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editProcess ? 'Edit' : 'Create'} Process</DialogTitle>
                <DialogDescription>Define a standard production process with quality checklist</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate}>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Process Name *</Label>
                      <Input
                        data-testid="process-name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                        placeholder="e.g., PIPE BENDING, WELDING"
                        required
                        style={{ textTransform: 'uppercase' }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Unit</Label>
                      <Input
                        data-testid="process-unit"
                        value={formData.unit}
                        onChange={(e) => setFormData({ ...formData, unit: e.target.value.toUpperCase() })}
                        placeholder="UNITS, HOURS, PCS"
                        style={{ textTransform: 'uppercase' }}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Benchmark Days</Label>
                      <Input
                        data-testid="process-benchmark-days"
                        type="number"
                        min="0"
                        value={formData.benchmark_days}
                        onChange={(e) => {
                          const days = parseInt(e.target.value) || 0;
                          setFormData(prev => ({
                            ...prev,
                            benchmark_days: days,
                            target_time_hours: days * 8
                          }));
                        }}
                        placeholder="Days before target date"
                      />
                      <p className="text-xs text-muted-foreground">
                        Days before WO target production date by which this process should complete
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Target Time (hours)</Label>
                      <Input
                        data-testid="process-target-time-hours"
                        type="number"
                        min="0"
                        step="0.5"
                        value={formData.target_time_hours}
                        onChange={(e) => setFormData({ ...formData, target_time_hours: parseFloat(e.target.value) || 0 })}
                        placeholder="Estimated hours per JC"
                      />
                      <p className="text-xs text-muted-foreground">
                        Auto-calculated as benchmark days x 8. Override manually if needed.
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        data-testid="process-description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value.toUpperCase() })}
                        rows={2}
                        placeholder="PROCESS DESCRIPTION..."
                        style={{ textTransform: 'uppercase' }}
                      />
                    </div>
                  </div>

                  {/* Checklist Section */}
                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold flex items-center gap-2">
                        <CheckSquare className="w-4 h-4" />
                        Quality Checklist ({formData.checklist.length} items)
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      These checklist items will appear on the Job Card for this process
                    </p>
                    
                    {/* Add new checklist item */}
                    <div className="flex gap-2">
                      <Input
                        value={newChecklistItem}
                        onChange={(e) => setNewChecklistItem(e.target.value.toUpperCase())}
                        placeholder="ENTER CHECKLIST ITEM..."
                        style={{ textTransform: 'uppercase' }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addChecklistItem();
                          }
                        }}
                      />
                      <Button type="button" variant="outline" onClick={addChecklistItem}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* Checklist items */}
                    {formData.checklist.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {formData.checklist.map((item, index) => (
                          <div key={index} className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                            <span className="text-sm font-mono text-muted-foreground w-6">{index + 1}.</span>
                            <span className="flex-1 text-sm">{item}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeChecklistItem(index)}
                            >
                              <X className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4 border rounded-lg">
                        No checklist items. Add items that workers should verify for this process.
                      </p>
                    )}
                  </div>

                  {/* Sub-Processes Section */}
                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold flex items-center gap-2">
                        <Layers className="w-4 h-4" />
                        Sub-Processes ({formData.sub_processes.length} items)
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Define sub-processes for labor tracking with benchmark production rates
                    </p>
                    
                    {/* Add new sub-process */}
                    <div className="flex gap-2">
                      <Input
                        value={newSubProcess.name}
                        onChange={(e) => setNewSubProcess({ ...newSubProcess, name: e.target.value.toUpperCase() })}
                        placeholder="SUB-PROCESS NAME..."
                        className="flex-1"
                        style={{ textTransform: 'uppercase' }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addSubProcess();
                          }
                        }}
                      />
                      <Input
                        type="number"
                        value={newSubProcess.benchmark_qty_per_hour}
                        onChange={(e) => setNewSubProcess({ ...newSubProcess, benchmark_qty_per_hour: e.target.value })}
                        placeholder="Qty/Hour"
                        className="w-24"
                      />
                      <Button type="button" variant="outline" onClick={addSubProcess}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* Sub-process items */}
                    {formData.sub_processes.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {formData.sub_processes.map((sp, index) => (
                          <div key={index} className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                            <span className="text-sm font-mono text-muted-foreground w-6">{index + 1}.</span>
                            <span className="flex-1 text-sm font-medium">{sp.name}</span>
                            <Badge variant="outline" className="text-xs">
                              {sp.benchmark_qty_per_hour} units/hr
                            </Badge>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeSubProcess(index)}
                            >
                              <X className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4 border rounded-lg">
                        No sub-processes defined. Add sub-processes for detailed labor tracking.
                      </p>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="process-submit">Save Process</Button>
                </DialogFooter>
              </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Process Register Table */}
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-10"></TableHead>
                <TableHead>Process Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-center">Unit</TableHead>
                <TableHead className="text-center">Benchmark Days</TableHead>
                <TableHead className="text-center">Target Hours</TableHead>
                <TableHead className="text-center">Sub-Processes</TableHead>
                <TableHead className="text-center">Checklist</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {processes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12">
                    <Settings className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">No processes defined yet</p>
                    <p className="text-sm text-muted-foreground mt-1">Create standard processes to use in BOMs</p>
                  </TableCell>
                </TableRow>
              ) : (
                processes.map((process) => {
                  const isExpanded = expandedProcess === process.id;
                  const checklistCount = process.checklist?.length || 0;
                  const subProcessCount = process.sub_processes?.length || 0;
                  const hasExpandableContent = checklistCount > 0 || subProcessCount > 0;
                  
                  return (
                    <React.Fragment key={process.id}>
                      {/* Main Process Row */}
                      <TableRow className="hover:bg-muted/30">
                        <TableCell>
                          {hasExpandableContent && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1 h-6 w-6"
                              onClick={() => toggleExpand(process.id)}
                            >
                              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Settings className="w-4 h-4 text-purple-600" />
                            <span className="font-medium">{process.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm max-w-xs truncate">
                          {process.description || '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{process.unit}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {process.benchmark_days > 0 ? (
                            <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                              {process.benchmark_days} days
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {process.target_time_hours > 0 ? (
                            <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                              {process.target_time_hours}h
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {subProcessCount > 0 ? (
                            <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                              {subProcessCount} items
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">None</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {checklistCount > 0 ? (
                            <Badge className="bg-green-100 text-green-700 border-green-200">
                              {checklistCount} items
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">None</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(process)}
                              data-testid={`edit-process-${process.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openDeleteDialog(process)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>

                      {/* Expanded Content */}
                      {isExpanded && hasExpandableContent && (
                        <TableRow className="bg-muted/20">
                          <TableCell colSpan={8} className="p-4">
                            <div className="grid grid-cols-2 gap-6">
                              {/* Sub-Processes */}
                              {subProcessCount > 0 && (
                                <div className="space-y-2">
                                  <p className="font-medium text-sm flex items-center gap-2">
                                    <Layers className="w-4 h-4 text-purple-600" />
                                    Sub-Processes ({subProcessCount} items)
                                  </p>
                                  <div className="space-y-1">
                                    {process.sub_processes.map((sp, idx) => (
                                      <div key={idx} className="flex items-center justify-between gap-2 text-sm p-2 bg-white rounded border">
                                        <div className="flex items-center gap-2">
                                          <span className="font-mono text-muted-foreground w-6">{idx + 1}.</span>
                                          <span className="font-medium">{sp.name}</span>
                                        </div>
                                        <Badge variant="outline" className="text-xs">
                                          {sp.benchmark_qty_per_hour} units/hr
                                        </Badge>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Checklist */}
                              {checklistCount > 0 && (
                                <div className="space-y-2">
                                  <p className="font-medium text-sm flex items-center gap-2">
                                    <CheckSquare className="w-4 h-4 text-green-600" />
                                    Quality Checklist ({checklistCount} items)
                                  </p>
                                  <div className="space-y-1">
                                    {process.checklist.map((item, idx) => (
                                      <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-white rounded border">
                                        <span className="font-mono text-muted-foreground w-6">{idx + 1}.</span>
                                        <span>{item}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Process</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete the process <strong>{deleteProcess?.name}</strong>?
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeleteProcess(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
